import React, { ReactNode } from 'react'

export enum MaintenanceAndSupportStatus {
  Current = 'Current',
  Expiring = 'Expiring',
  Expired = 'Expired',
  None = 'None',
}

// NOTE: exercise caution with the date fields below as they may be strings
export interface AccountDetails {
  // Note this should only be used when wanting system detected override, otherwise use product in SystemInfo
  paperCutEdition: string
  paperCutVersion: string
  maintenanceAndSupportStatus: MaintenanceAndSupportStatus
  maintenanceAndSupportExpiryDate?: Date
  organisationType: string
  licenseType: string
  registeredUsers: string
  licensedMFDs?: any
  licensedSFPs?: any
  licensedConnectors?: any
  organisation: string
  licensedReleaseStations: number
  crn: string
  lastUpdate: Date
  licenseExpiryDate?: Date
  orderReference: string
  growsTreesPlanted: number
}

export const NOT_AVAILABLE: string = 'N/A'

export const TRIAL = 'Trial'
export const NFR = 'NFR'

export class AccountDataUtil {
  static getLicensedDeviceCategoryTypeAndCounts = (
    type: string,
    typeMap?: any,
    account?: AccountDetails
  ): ReactNode => {
    if (!account) {
      return null
    }

    if (account.licenseType === TRIAL || account.licenseType === NFR) {
      return (
        <>
          <dt>{type}</dt>
          <dd>Unlimited</dd>
        </>
      )
    }

    if (typeMap && Object.keys(typeMap).length > 0) {
      return (
        <>
          <dt>{type}</dt>
          <dd>{AccountDataUtil.getLicensedDeviceCategoryCounts(true, typeMap, account)}</dd>
        </>
      )
    }

    return null
  }

  static getLicensedDeviceCategoryCounts = (showUsed: boolean, typeMap?: any, account?: AccountDetails): ReactNode => {
    if (!account) {
      return null
    }

    if (account.licenseType === TRIAL || account.licenseType === NFR) {
      return <>Unlimited</>
    }

    if (typeMap && Object.keys(typeMap).length > 0) {
      return (
        <>
          {Array.from(Object.keys(typeMap)).map(dev => {
            let devLicensed = typeMap[dev]
            if (devLicensed === undefined) {
              return null
            }

            return (
              <React.Fragment key={dev}>
                {dev}{' '}
                {showUsed && (
                  <>
                    {devLicensed.used} / {devLicensed.licensed}
                  </>
                )}{' '}
                {!showUsed && <>({devLicensed.licensed})</>}
                <br />
              </React.Fragment>
            )
          })}
        </>
      )
    }

    return null
  }

  static getAccountDetailsFromResponse = (data: any) => {
    let mnsExpiryDate = undefined
    if (data.maintenanceSupportExpiryDate != null) {
      mnsExpiryDate = new Date(data.maintenanceSupportExpiryDate)
    }

    let licenseExpiryDate = undefined
    if (data.licenseExpiryDate != null) {
      licenseExpiryDate = new Date(data.licenseExpiryDate)
    }

    return {
      licensedMFDs: data.licensedMfds,
      licensedSFPs: data.licensedSfps,
      licensedConnectors: data.licensedConnectors,
      organisationType: data.orgType,
      licenseType: data.licenseType,
      registeredUsers: data.registeredUsers,
      maintenanceAndSupportStatus: data.maintenanceSupportStatus,
      maintenanceAndSupportExpiryDate: mnsExpiryDate,
      paperCutEdition: data.pcEdition,
      paperCutVersion: data.paperCutVersion,
      organisation: data.org,
      licensedReleaseStations: data.licensedReleaseStations,
      crn: data.crn,
      lastUpdate: new Date(data.lastUpdate),
      licenseExpiryDate: licenseExpiryDate,
      orderReference: data.orderReference,
      growsTreesPlanted: data.growsTreesPlanted,
    }
  }

  // AccountDetails dates are typed as Date but at runtime can be ISO strings,
  // depending on which path put them into Redux. Coerce defensively.
  static toDate(d: unknown): Date | undefined {
    if (d === undefined || d === null) return undefined
    if (d instanceof Date) return d
    if (typeof d === 'string' || typeof d === 'number') {
      const parsed = new Date(d)
      return isNaN(parsed.getTime()) ? undefined : parsed
    }
    return undefined
  }
}

export type AddCustomerFunction = (product: string, customer: Customer | PmitcCustomer) => Promise<boolean>

export type EditCustomerFunction = (
  product: string,
  customer: Customer | PmitcCustomer,
  customerId?: string
) => Promise<boolean>

export type CustomerFunction = (
  product: string,
  customer: Customer | PmitcCustomer,
  customerId?: string
) => Promise<boolean>

export interface Customer {
  name: string
  product: string
  infrastructure: string
  tags: Array<string>
}

export interface PmitcCustomer extends Customer {
  existingOrg: boolean
  orgId: string
  dataCenter: string
  timezone: string
  crn: string
  customer: {
    name: string
    industry: string
    website: string
    country: string
  }
}

export enum Product {
  Pocket = 'pocket',
  MF = 'mf',
  NG = 'ng',
  Hive = 'hive',
}

export interface ConnectorDownloadURLs {
  customerConnectorDownloadURL: string
  windowsDownloadURL: string
  linuxDownloadURL: string
}

export interface HostData {
  id: string
  name: string
  product: string
  remoteManageLink: string
  infrastructure: string
  tags?: string[]
  industry?: string
}

export enum HostEventType {
  Error = 'Error',
  Warning = 'Warning',
  Good = 'Good',
  Remote = 'Remote',
  License = 'License',
  Customer = 'Customer',
}

export interface HostEvent {
  type: HostEventType
  message: string
  occurredAt: Date
}

export enum ConnectionStatus {
  Live,
  Disconnected,
  Disabled,
}

export enum HostStatus {
  Unlinked,
  MonitoringDisabled,
  MaintenanceAndSupportRequired,
  StaleData,
  Good,
}

export class HostDataUtil {
  static getInfrastructureText(infrastructure: string) {
    switch (infrastructure) {
      case 'local':
        return 'Hosted locally'
      case 'azure':
        return 'Hosted on Azure'
      case 'aws':
        return 'Hosted on AWS'
      case 'gcp':
        return 'Hosted on Google Cloud Platform'
      case 'papercut':
        return 'Hosted on PaperCut Cloud Services'
      default:
        return infrastructure ? 'Hosted on ' + infrastructure : 'Hosted locally'
    }
  }

  static getVersion = (systemInfo?: SystemInfoData, accountDetails?: AccountDetails, condensed: boolean = false) => {
    let ver = systemInfo?.version ?? accountDetails?.paperCutVersion ?? ''
    return condensed ? ver.replace(/\(.*\)/, '') : ver
  }

  static newVersionRequired = (version: string) => {
    if (!version) return true
    const majorVersionEndIndex = version.indexOf('.')
    const majorVersion = +version.substring(0, majorVersionEndIndex)
    // only care about the first digit
    const minorPart = +version.slice(majorVersionEndIndex + 1, majorVersionEndIndex + 2)
    return majorVersion + minorPart / 10 < 19.1
  }

  static getInfrastructureShortName = (infrastructure: string) => {
    switch (infrastructure) {
      case 'aws':
        return 'AWS'
      case 'gcp':
        return 'GCP'
      case 'azure':
        return 'Azure'
      case 'local':
        return 'Local'
      case 'papercut':
        return 'PaperCut'
      default:
        return infrastructure
    }
  }

  static getProductText(product: string) {
    switch (product) {
      default:
        return 'PaperCut ' + product
      case Product.Pocket:
        return 'PaperCut Pocket'
      case Product.MF:
        return 'PaperCut MF'
      case Product.NG:
        return 'PaperCut NG'
      case Product.Hive:
        return 'PaperCut Hive'
    }
  }

  static isPmitc(product: string) {
    return product === Product.Hive || product === Product.Pocket
  }
}

export interface SystemInfoData {
  product: string
  version: string // do not use this directly - use HostDataUtil.getVersion()
  osPlatform: string
  embeddedCount: number
}
